import {
  IFieldInstance,
  IncidentStatus,
} from "../../../types/fieldInstance.interface";
import {
  removeNonFormValues,
  transformForSubmit,
} from "../../../utils/formGenUtils/transformForSubmit";
import { transformedInlineRLFields } from "../../../utils/helpers/relatedListHelpers";
import { toastService } from "../../../utils/helpers/toastService";

export function closeIncidentCreation(
  hasUnsavedChanges: boolean,
  setIncidentFormState: (payload: any) => void,
  handleDiscard: () => void
) {
  if (hasUnsavedChanges) {
    setIncidentFormState({ showPrompt: true });
  } else {
    handleDiscard();
  }
}

export function resetFormState(
  formState: any,
  mergeTabState: (payload: { [tab: string]: any }) => void
): void {
  // Clear each tab's dirty flag via a per-tab merge so a concurrent validity
  // write isn't clobbered by a full-tabState replace from this snapshot.
  const payload = Object.keys(formState).reduce((acc, key) => {
    acc[key] = { isFormDirty: false };
    return acc;
  }, {} as any);
  mergeTabState(payload);
}

export function resetFormOnAction(
  resetForm: (x: any) => void,
  values: any,
  formState: any,
  mergeTabState: (payload: { [tab: string]: any }) => void,
  updateFormData: (x: any) => void
) {
  resetForm({ values });
  updateFormData(values);
  resetFormState(formState, mergeTabState);
}

const getResidentName = (residentId: string, residentsInFacility: any[]) => {
  return residentsInFacility.find((r) => r.value === residentId)?.label;
};

const getStageSavedAt = (stages: any[], tabTitle: string) => {
  return stages.find((stage: { label: string }) => stage.label === tabTitle)
    ?.label;
};

export interface FormStateValues {
  guestName: string;
  ssnLastFourDigits: string;
  agency: string;
  shiftWorked: string;
  unitAssignment: string;
  incidentTypeFlag: string;
  incidentCreatedBy: string | undefined;
  userId: string;
  userName: string;
  initialValues: { [key: string]: any } | undefined;
  deleteRelatedRecords: { Sobject: string; Id: string }[];
  markedAsHighSeverity: boolean;
}

const getInlineRLIds = (values: any) => {
  const ids = {} as any;
  const allFieldNames = Object.keys(values);
  const allInlineRLFieldNames = Object.keys(transformedInlineRLFields).map(
    (fieldName) => `${fieldName}_id`
  );
  allFieldNames.forEach((fieldName) => {
    if (allInlineRLFieldNames.includes(fieldName)) {
      ids[fieldName] = values[fieldName];
    }
  });
  return ids;
};

const populateValuesForInlineRLs = (
  values: any,
  formStateValues: FormStateValues
) => {
  Object.keys(transformedInlineRLFields).forEach((fieldName) => {
    const obj = Object.keys(transformedInlineRLFields[fieldName]).reduce(
      (acc, field) => {
        acc[field] = values["incident"][field];
        return acc;
      },
      {} as { [key: string]: string }
    );
    const _formStateValues: any = { ...formStateValues };
    const recordId = _formStateValues?.[`${fieldName}_id`];
    values[fieldName] = recordId ? { ...obj, Id: recordId } : { ...obj };
  });
  return values;
};

const populateValues = (
  values: any,
  formStateValues: FormStateValues,
  isIncidentSubmit: boolean = false,
  stages?: any[],
  residentsInFacility?: any[],
  tabTitle?: string
) => {
  const {
    guestName,
    ssnLastFourDigits,
    agency,
    shiftWorked,
    unitAssignment,
    incidentTypeFlag,
    incidentCreatedBy,
    userId,
    userName,
    markedAsHighSeverity,
  } = formStateValues;
  values["incident"]["RecordTypeId"] = incidentTypeFlag;
  const residentId = values["incident"]["Resident__c"] || "";
  if (!isIncidentSubmit && stages && residentsInFacility && tabTitle) {
    values["incident"]["incidentStatus"] = "Draft";
    values["incident"]["stageSavedAt"] = getStageSavedAt(stages, tabTitle);
    values["incident"]["residentName"] = getResidentName(
      residentId,
      residentsInFacility
    );
    values["incident"]["Draft_Created_By__c"] =
      values["incident"]["Draft_Created_By__c"] ?? userName;
  }
  values["incident"]["Incident_Created_By__c"] = incidentCreatedBy ?? userId;
  values["incident"]["Mark_as_High_Severity__c"] = markedAsHighSeverity;
  if (incidentCreatedBy) {
    // If incidentCreatedBy already has a value, the incident is being modified
    values["incident"]["Modified_By__c"] = userId;
  }

  values["guestUserIncidentCreationQuestion"] = {
    Full_Name__c: guestName,
    Last_Four_digits_of_SSN__c: ssnLastFourDigits,
    Name_of_Agency__c: agency,
    Shift_Worked__c: shiftWorked,
    Unit_Assignment__c: unitAssignment,
  };
  values["deleteRelatedRecords"] = formStateValues.deleteRelatedRecords;
  const _values = populateValuesForInlineRLs(values, formStateValues);

  return _values;
};

interface FormSubmitStates {
  createIncidentLoading: boolean;
  isLastStep: boolean;
  canSubmitForm: boolean;
  editModeActive: boolean;
  previousRoute: string | null;
  isDraft: boolean;
}

export interface AllFormFields {
  allFields: IFieldInstance[];
  incidentRelatedLists: any;
}

// MARCC-785: only a real Incident__c Id may be forwarded on submit. A loaded draft's Id is
// its Incident_Draft__c record Id, so drafts must never send it and instead insert a new incident.
export function shouldAttachIncidentId(
  mergedValues: { Id?: string },
  isDraft: boolean
): boolean {
  return Boolean(mergedValues.Id) && !isDraft;
}

export async function submitIncidentForm(
  values: { [key: string]: any },
  formStateValues: FormStateValues,
  formSubmitStates: FormSubmitStates,
  resetForm: (values: any) => void,
  incidentFormData: { [key: string]: any } | null,
  allFormFields: AllFormFields,
  createMutateAsync: any,
  onSuccess: (
    values: any,
    previousRoute: string | null,
    shouldResetForm: boolean,
    resetForm: (values: any) => void,
    resetFormState: (formState: any, setFormState: (x: any) => void) => void,
    navRoute?: string
  ) => void
) {
  const { incidentTypeFlag, initialValues } = formStateValues;
  const {
    createIncidentLoading,
    isLastStep,
    canSubmitForm,
    previousRoute,
    isDraft,
  } = formSubmitStates;
  const { allFields, incidentRelatedLists } = allFormFields;
  if (createIncidentLoading) return;
  const shouldSubmit = isLastStep || canSubmitForm;
  if (shouldSubmit && incidentTypeFlag && initialValues) {
    // Merge stored form data from previous tabs with current formik values.
    // incidentFormData contains user edits saved during tab navigation;
    // current formik values (from the active tab) take precedence.
    const mergedValues = incidentFormData
      ? { ...incidentFormData, ...values }
      : values;
    // consolidate values from state
    const formValues = removeNonFormValues(initialValues, mergedValues);
    let transformedValues = transformForSubmit(
      formValues,
      allFields,
      incidentRelatedLists
    );
    const incidentCreatedBy = !isDraft
      ? mergedValues?.Incident_Created_By__c
      : undefined;
    const inlineRLIds = getInlineRLIds(mergedValues);
    const _formStateValues = {
      ...formStateValues,
      ...inlineRLIds,
      incidentCreatedBy,
    };
    // MARCC-776: attach the record Id whenever the incident already exists, so a resubmit
    // UPDATES it instead of inserting a duplicate. Previously a status whitelist omitted
    // "Signature in Progress", so resubmitting a signed incident dropped the Id and the
    // backend inserted a copy. A brand-new incident has no Id and still inserts as normal.
    // MARCC-785: skip drafts — mergedValues.Id there is the Incident_Draft__c record Id, which
    // the backend can't resolve to an Incident__c ("List has no rows"), so a draft must insert.
    if (shouldAttachIncidentId(mergedValues, isDraft)) {
      transformedValues["incident"]["Id"] = mergedValues.Id;
    }
    transformedValues = populateValues(
      transformedValues,
      _formStateValues,
      true
    );
    const route = "/user/incidents-list";
    const message = `Incident ${
      !mergedValues?.Incident_Status__c ? "Creation" : "Update"
    } Successful`;
    const shouldResetForm =
      mergedValues?.Incident_Status__c === IncidentStatus.Submitted;
    const req = { data: transformedValues, isDraft };
    const result = await createMutateAsync(req, {
      onSuccess: (res: any) => {
        toastService.success(`${res?.data?.message}` || message);
        onSuccess(
          mergedValues,
          previousRoute,
          shouldResetForm,
          resetForm,
          resetFormState,
          route
        );
      },
      onError: (error: any) => {
        // Surface the backend message (validation-rule errors are user-friendly); fall back otherwise.
        toastService.error(
          error?.response?.data?.message || "Something went wrong"
        );
      },
    });
    return result;
    // reset saved values on successful submission
  } else {
    toastService.error("Something went wrong");
  }
}

export async function saveIncidentForm(
  values: { [x: string]: any },
  formStateValues: FormStateValues,
  allFormFields: AllFormFields,
  formStages: { stages: any[]; tabTitle: string },
  selectOptions: { [x: string]: any },
  saveMutateAsync: any,
  onSaveSuccess: (values: any, res: any) => void
) {
  const { CreatedBy, ...draftValues } = values;
  const { allFields, incidentRelatedLists } = allFormFields;
  const { stages, tabTitle } = formStages;
  let _values = transformForSubmit(
    draftValues,
    allFields,
    incidentRelatedLists
  );
  const residentsInFacility =
    selectOptions.allLookUpOptions.residentsInFacility;
  const incidentCreatedBy = draftValues?.Incident_Created_By__c;
  const _formStateValues = { ...formStateValues, incidentCreatedBy };
  _values = populateValues(
    _values,
    _formStateValues,
    false,
    stages,
    residentsInFacility,
    tabTitle
  );
  const saveResult = await saveMutateAsync(
    {
      ..._values,
    },
    {
      onSuccess: (res: any) => {
        onSaveSuccess(values, res);
        toastService.success("Saved successfully");
      },
      onError: () => {
        toastService.error("Something went wrong");
      },
    }
  );
  return saveResult;
}

export async function setIncidentStatusToOpen(
  setStatusToOpen: any,
  id: string,
  loggedInUserId: string,
  setIncidentFormState: (val: any) => void
) {
  await setStatusToOpen(
    { incidentId: id, loggedInUserId },
    {
      onSuccess: (res: any) => {
        setIncidentFormState({ incidentStatus: IncidentStatus.Open });
        toastService.success(
          res?.data?.message || "Status updated successfully"
        );
      },
      onError: (res: any) => {
        toastService.error(res?.data?.message || "Status Update Failed");
      },
    }
  );
}

export async function handleDeleteDrafts(
  values: { [x: string]: any },
  allFormFields: AllFormFields,
  handleDiscard: () => void,
  deleteMutateAsync: any
) {
  const req = values;
  const { allFields, incidentRelatedLists } = allFormFields;
  let _values = transformForSubmit(req, allFields, incidentRelatedLists);
  try {
    const response = await deleteMutateAsync(_values);
    toastService.success(response.data.message || "Draft deleted successfully");
    handleDiscard();
  } catch (err: any) {
    toastService.error(err.response?.data?.message || "Delete action failed!");
  }
}

export function handleNextClick(
  shouldToastRender: boolean,
  tabTitle: string,
  mergeTabState: (payload: { [tab: string]: any }) => void,
  handleNavigation: (path: string) => void,
  path: string,
  setIsSubmitting: (value: boolean) => void
): void {
  setIsSubmitting(true);
  if (shouldToastRender) {
    toastService.error("Please fix errors with fields");
  } else {
    // Merge just this tab's visited flag so we don't overwrite validity a
    // parallel revalidation set with a full-tabState snapshot.
    mergeTabState({ [tabTitle]: { visited: true } });
    handleNavigation(path);
  }
}

export const defaultState = {
  baseFormData: {
    facilityId: "",
    residentId: "",
    incidentType: "",
    guestName: "",
    ssnLastFourDigits: "",
    agency: "",
    shiftWorked: "",
    unitAssignment: "",
  },
  incidentFormData: null,
  formState: {
    incidentStatus: "",
    tabState: undefined,
    hasUnsavedChanges: false,
    shouldResetForm: false,
    showPrompt: false,
    clickedStage: "",
    exitLink: "",
  },
};
